import React, { useState } from 'react';
import { Button, Typography, Card, Row, Col, Progress, Badge, Image, message, Spin, Space } from 'antd';
import { 
  CameraOutlined, 
  CheckCircleOutlined, 
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
  CloudUploadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { 
  addPhoto, 
  removePhoto,
  updatePhotoStatus,
  PhotoTypes,
  getRequiredPhotoTypes,
  getOptionalPhotoTypes,
  getCompletedRequiredPhotosCount,
  getRequiredPhotosCount,
  VehiclePhoto
} from '../../../redux/reducers/vehiclePhotosSlice';
import { Camera } from '../../../components/Camera';
import Api from '../../../services/api';
import './PhotoCaptureStep.css';

const { Title, Text } = Typography;

interface PhotoCaptureStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

interface PhotoCard {
  type: PhotoTypes;
  label: string;
  required: boolean;
  icon?: string;
  description?: string;
}

const PhotoCaptureStep: React.FC<PhotoCaptureStepProps> = ({ 
  onNext, 
  onPrevious 
}) => {
  const dispatch = useAppDispatch();
  const { photoMap } = useAppSelector((state) => state.vehiclePhotos);
  const [showCamera, setShowCamera] = useState(false);
  const [currentPhotoType, setCurrentPhotoType] = useState<PhotoTypes | null>(null);
  const [uploadingPhotos, setUploadingPhotos] = useState<Set<PhotoTypes>>(new Set());
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const requiredPhotos = getRequiredPhotoTypes();
  const optionalPhotos = getOptionalPhotoTypes();
  const completedRequired = getCompletedRequiredPhotosCount(photoMap);
  const totalRequired = getRequiredPhotosCount();
  const progressPercent = Math.round((completedRequired / totalRequired) * 100);

  const photoCardIcons: Record<PhotoTypes, { icon: string; description: string }> = {
    [PhotoTypes['Damage Wide-angle']]: { 
      icon: '📐', 
      description: 'Wide shot showing full damage area' 
    },
    [PhotoTypes['Damage Close-up']]: { 
      icon: '🔍', 
      description: 'Close-up detail of damage' 
    },
    [PhotoTypes['Front Driverside']]: { 
      icon: '🚗', 
      description: 'Front left corner view' 
    },
    [PhotoTypes['Front Passenger']]: { 
      icon: '🚗', 
      description: 'Front right corner view' 
    },
    [PhotoTypes['Rear Driverside']]: { 
      icon: '🚙', 
      description: 'Rear left corner view' 
    },
    [PhotoTypes['Rear Passenger']]: { 
      icon: '🚙', 
      description: 'Rear right corner view' 
    },
    [PhotoTypes['Vin']]: { 
      icon: '🔢', 
      description: 'VIN barcode or plate' 
    },
    [PhotoTypes['Odometer']]: { 
      icon: '⏱️', 
      description: 'Dashboard odometer reading' 
    },
    [PhotoTypes['Lic Plate']]: { 
      icon: '🎫', 
      description: 'License plate number' 
    },
    [PhotoTypes['Additional Photo 1']]: { 
      icon: '📸', 
      description: 'Any additional relevant photo' 
    },
    [PhotoTypes['Additional Photo 2']]: { 
      icon: '📸', 
      description: 'Any additional relevant photo' 
    },
    [PhotoTypes['Additional Photo 3']]: { 
      icon: '📸', 
      description: 'Any additional relevant photo' 
    },
    [PhotoTypes['Additional Photo 4']]: { 
      icon: '📸', 
      description: 'Any additional relevant photo' 
    },
  };

  const photoCards: PhotoCard[] = [
    ...requiredPhotos.map(type => ({
      type,
      label: PhotoTypes[type],
      required: true,
      icon: photoCardIcons[type]?.icon || '📷',
      description: photoCardIcons[type]?.description || 'Capture photo'
    })),
    ...optionalPhotos.map(type => ({
      type,
      label: PhotoTypes[type],
      required: false,
      icon: photoCardIcons[type]?.icon || '📷',
      description: photoCardIcons[type]?.description || 'Optional photo'
    }))
  ];

  const handleTakePhoto = (type: PhotoTypes) => {
    setCurrentPhotoType(type);
    setShowCamera(true);
  };

  const handleUploadPhoto = (type: PhotoTypes) => {
    setCurrentPhotoType(type);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || currentPhotoType === null) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      message.error('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const imageData = e.target?.result as string;
      await handleCameraCapture(imageData);
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    event.target.value = '';
  };

  const dataURItoBlob = (dataURI: string): Blob => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const compressImage = async (imageData: string, maxWidth: number = 1920): Promise<string> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = imageData;
    });
  };

  const uploadPhotoToAzure = async (photoType: PhotoTypes, imageData: string): Promise<string> => {
    try {
      // Update status to uploading
      dispatch(updatePhotoStatus({ type: photoType, status: 'uploading' }));
      setUploadingPhotos(prev => new Set(prev).add(photoType));

      // Compress image
      const compressedImage = await compressImage(imageData);
      const blob = dataURItoBlob(compressedImage);

      // Get signing options from API
      const signingOptions = await Api.getFileSigningOptions();
      const fileName = `incident_${Date.now()}_${PhotoTypes[photoType]}.jpg`;
      const uploadUrl = `${signingOptions.uploadUrl}/${signingOptions.containerName}/${fileName}?${signingOptions.sharedAccessSignature}`;

      // Upload to Azure
      await Api.uploadPhotoToAzure(uploadUrl, blob);

      // Update status to completed
      dispatch(updatePhotoStatus({ type: photoType, status: 'completed' }));
      
      // Return the URL without SAS token for storage
      const finalUrl = `${signingOptions.uploadUrl}/${signingOptions.containerName}/${fileName}`;
      return finalUrl;
    } catch (error) {
      console.error('Failed to upload photo:', error);
      dispatch(updatePhotoStatus({ 
        type: photoType, 
        status: 'failed',
        error: 'Upload failed'
      }));
      throw error;
    } finally {
      setUploadingPhotos(prev => {
        const newSet = new Set(prev);
        newSet.delete(photoType);
        return newSet;
      });
    }
  };

  const handleCameraCapture = async (imageData: string) => {
    if (currentPhotoType !== null) {
      try {
        // Add photo to store immediately
        dispatch(addPhoto({
          type: currentPhotoType,
          photo: {
            uri: imageData,
            type: currentPhotoType,
            uploadStatus: 'pending'
          }
        }));
        
        setShowCamera(false);
        setCurrentPhotoType(null);
        
        // Upload to Azure in background
        const finalUrl = await uploadPhotoToAzure(currentPhotoType, imageData);
        
        // Update photo with final URL
        const updatedPhoto: VehiclePhoto = {
          uri: imageData,
          type: currentPhotoType,
          finalUrl: finalUrl,
          filename: `incident_${Date.now()}_${PhotoTypes[currentPhotoType]}.jpg`,
          base64: imageData.split(',')[1],
          uploadStatus: 'completed'
        };
        
        dispatch(addPhoto({
          type: currentPhotoType,
          photo: updatedPhoto as any
        }));
        
        message.success('Photo captured and uploaded successfully');
      } catch (error) {
        message.error('Photo captured but upload failed. It will be retried later.');
      }
    }
  };

  const handleDeletePhoto = (type: PhotoTypes) => {
    dispatch(removePhoto(type));
    message.info('Photo removed');
  };

  const handleContinue = () => {
    if (completedRequired < totalRequired) {
      message.warning(`Please capture all required photos (${completedRequired}/${totalRequired} completed)`);
      return;
    }
    onNext();
  };

  const renderPhotoCard = (card: PhotoCard) => {
    const photo = photoMap[card.type];
    const hasPhoto = !!photo;
    const isUploading = uploadingPhotos.has(card.type);

    return (
      <Col xs={24} sm={12} md={8} lg={6} key={card.type}>
        <Badge.Ribbon 
          text={card.required ? "Required" : "Optional"} 
          color={card.required ? "orange" : "blue"}
          style={{ display: hasPhoto ? 'none' : 'block' }}
        >
          <Card
            className={`photo-card ${hasPhoto ? 'has-photo' : ''} ${card.required ? 'required' : 'optional'}`}
            hoverable={!isUploading}
            bordered
            cover={
            hasPhoto && photo ? (
              <div className="photo-preview">
                <Image
                  src={photo.uri}
                  alt={card.label}
                  preview={{
                    mask: <EyeOutlined />
                  }}
                />
                {isUploading && (
                  <div className="upload-overlay">
                    <Spin 
                      indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      tip="Uploading..."
                    />
                  </div>
                )}
                {photo.uploadStatus === 'completed' && (
                  <div className="upload-status-badge">
                    <CloudUploadOutlined style={{ color: '#52c41a' }} />
                  </div>
                )}
                {photo.uploadStatus === 'failed' && (
                  <div className="upload-status-badge failed">
                    <CloudUploadOutlined style={{ color: '#ff4d4f' }} />
                  </div>
                )}
                <div className="photo-overlay">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(card.type);
                    }}
                    disabled={isUploading}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div className="photo-placeholder">
                <div className="placeholder-content">
                  <div className="photo-type-icon">{card.icon}</div>
                  <h4 className="photo-type-label">{card.label}</h4>
                  <p className="photo-type-description">{card.description}</p>
                </div>
                <div className="photo-action-bar">
                  <div 
                    className="action-camera"
                    onClick={() => handleTakePhoto(card.type)}
                  >
                    <CameraOutlined style={{ marginRight: 6 }} />
                    <span>Tap to take photo</span>
                  </div>
                  <div 
                    className="action-upload"
                    onClick={() => handleUploadPhoto(card.type)}
                    title="Upload from gallery"
                  >
                    <UploadOutlined />
                    <span className="upload-text">Upload</span>
                  </div>
                </div>
              </div>
            )
          }
        >
          <Card.Meta
            title={
              <span>
                {card.label}
                {card.required && (
                  <Badge 
                    status={hasPhoto ? "success" : "default"} 
                    style={{ marginLeft: 8 }}
                  />
                )}
              </span>
            }
            description={
              hasPhoto && photo ? (
                photo.uploadStatus === 'uploading' ? 'Uploading...' :
                photo.uploadStatus === 'completed' ? 'Uploaded' :
                photo.uploadStatus === 'failed' ? 'Upload failed' :
                card.required ? "Required" : "Optional"
              ) : (
                card.required ? "Required" : "Optional"
              )
            }
          />
          </Card>
        </Badge.Ribbon>
      </Col>
    );
  };

  return (
    <div className="step-container photo-capture-container">
      <Title level={3}>Vehicle Photos</Title>
      <Text type="secondary">Capture photos of the vehicle from all required angles</Text>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <div className="photo-progress">
        <Text>Required Photos Progress</Text>
        <Progress 
          percent={progressPercent} 
          status={completedRequired === totalRequired ? 'success' : 'active'}
          format={() => `${completedRequired}/${totalRequired}`}
        />
      </div>

      <div className="photo-sections">
        <Title level={4}>Required Photos</Title>
        <Row gutter={[16, 16]}>
          {photoCards.filter(card => card.required).map(renderPhotoCard)}
        </Row>

        <Title level={4} style={{ marginTop: 32 }}>Optional Photos</Title>
        <Row gutter={[16, 16]}>
          {photoCards.filter(card => !card.required).map(renderPhotoCard)}
        </Row>
      </div>

      <Camera
        visible={showCamera}
        onCapture={handleCameraCapture}
        onClose={() => {
          setShowCamera(false);
          setCurrentPhotoType(null);
        }}
        title={currentPhotoType !== null ? `Capture ${PhotoTypes[currentPhotoType]}` : 'Take Photo'}
      />

      <div className="step-actions" style={{ marginTop: 40 }}>
        <Button onClick={onPrevious}>
          Previous
        </Button>
        <Button 
          type="primary" 
          icon={<CheckCircleOutlined />}
          onClick={handleContinue}
          disabled={completedRequired < totalRequired}
        >
          Continue ({completedRequired}/{totalRequired} Required)
        </Button>
      </div>
    </div>
  );
};

export default PhotoCaptureStep;